import React from "react";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const TRACKING_SEASON_NAMES = {
  0: "Other",
  1: "Spring",
  2: "Summer",
  3: "Fall",
  4: "Winter",
};

// Format an assigned-season date span from the bucket's min_start / max_end.
// Dates arrive as ISO strings (SQL Server datetime -> JSON); render in UTC so a
// midnight-UTC date doesn't slip a day in the viewer's local timezone.
function formatEvidenceDate(value) {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function assignedDateRangeLabel(bucket) {
  const start = formatEvidenceDate(bucket.min_start);
  const end = formatEvidenceDate(bucket.max_end);
  if (!start && !end) return null;
  if (start && end) return start === end ? start : `${start} – ${end}`;
  return start || end;
}

// Guidance hint: what the bucket's currently-assigned rows say (the old inferred
// value). Used to help set the declared mapping and to drive the soft mismatch
// warning. Returns { label, singleSeason }.
function inferredAssigned(bucket) {
  if (!bucket.assigned_season_count) {
    return { label: "No seasons assigned yet", singleSeason: null };
  }
  if (bucket.min_trackingseason !== bucket.max_trackingseason) {
    return { label: "Assigned seasons: mixed", singleSeason: null };
  }
  const s = bucket.min_trackingseason;
  return { label: `Assigned seasons: ${TRACKING_SEASON_NAMES[s] || s}`, singleSeason: s };
}

export default function SeasonSequenceBucketRow(props) {
  const bucket = props.bucket;
  const isEditing = props.isEditing;
  const editValue = props.editValue;
  const saving = props.saving;
  const error = props.error;
  const onEditClick = props.onEditClick;
  const onCancelClick = props.onCancelClick;
  const onSaveClick = props.onSaveClick;
  const onEditValueChange = props.onEditValueChange;
  const editMapsTo = props.editMapsTo;
  const onEditMapsToChange = props.onEditMapsToChange;
  const isToggling = props.isToggling;
  const toggleError = props.toggleError;
  const onToggleActiveClick = props.onToggleActiveClick;
  const isDeleting = props.isDeleting;
  const deleteError = props.deleteError;
  const onDeleteClick = props.onDeleteClick;
  const isOther = props.isOther;
  const canMoveUp = props.canMoveUp;
  const canMoveDown = props.canMoveDown;
  const isReordering = props.isReordering;
  const onMoveUp = props.onMoveUp;
  const onMoveDown = props.onMoveDown;

  const trimmedValue = (editValue || "").trim();
  const isBlank = trimmedValue.length === 0;
  const nameChanged = !isBlank && trimmedValue !== bucket.sequence_name;
  const currentMapsToStr = bucket.maps_to_trackingseason == null ? "" : String(bucket.maps_to_trackingseason);
  const mapsToChanged = String(editMapsTo ?? "") !== currentMapsToStr;
  const saveDisabled = saving || isBlank || (!nameChanged && !mapsToChanged);

  const inferred = inferredAssigned(bucket);
  const assignedDateRange = assignedDateRangeLabel(bucket);
  const declaredMapsToLabel = bucket.maps_to_trackingseason == null
    ? null
    : (TRACKING_SEASON_NAMES[bucket.maps_to_trackingseason] || `Season ${bucket.maps_to_trackingseason}`);
  const mismatchWarning = (isEditing && editMapsTo !== "" && editMapsTo != null && inferred.singleSeason != null && Number(editMapsTo) !== Number(inferred.singleSeason))
    ? `Assigned seasons are ${TRACKING_SEASON_NAMES[inferred.singleSeason]}; mapping to ${TRACKING_SEASON_NAMES[Number(editMapsTo)]} will flag them as Mismatch in Audit.`
    : null;

  // Delete is only offered for never-assigned buckets (assigned_season_count = 0);
  // once a bucket has history, activate/deactivate is the only lifecycle action --
  // deactivating a referenced bucket is allowed (it doesn't orphan those season
  // rows, see phase-9-deactivate-rule-correction.md), so this button is never
  // blocked, only disabled transiently while a toggle request is in flight.
  const isUnused = bucket.assigned_season_count === 0;

  const actionError = toggleError || deleteError;

  return (
    <TableRow>
      <TableCell>
        {!isOther && (
          <Stack direction="column" spacing={0} alignItems="center">
            <IconButton size="small" aria-label="move up" onClick={onMoveUp} disabled={!canMoveUp || isReordering}>
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label="move down" onClick={onMoveDown} disabled={!canMoveDown || isReordering}>
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <TextField
            size="small"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            error={!!error}
            helperText={error || ""}
            disabled={saving}
            autoFocus
          />
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <span>{bucket.sequence_name}</span>
            {bucket.review_state === "UNMAPPED" && (
              <Tooltip title="This bucket has no Maps To (Tracking Season). Map it in Setup before assigning seasons.">
                <Chip size="small" color="error" variant="outlined" label="unmapped" />
              </Tooltip>
            )}
            {bucket.review_state === "MAPPING_MISMATCH" && (
              <Tooltip
                title={`Assigned seasons are audited ${bucket.expected_season_name || "a different season"}, but this bucket maps to ${declaredMapsToLabel || "—"}. Set Maps To to ${bucket.expected_season_name || "the audited season"} — the audited value is the source of truth.`}
              >
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={bucket.expected_season_name ? `maps ≠ audited · needs ${bucket.expected_season_name}` : "maps ≠ audited"}
                />
              </Tooltip>
            )}
            {bucket.review_state === "SEASON_CONFLICT" && (
              <Tooltip title="Assigned seasons were audited to more than one Tracking Season. Split this bucket so each maps to its own audited season.">
                <Chip size="small" color="warning" variant="outlined" label="mixed audited seasons" />
              </Tooltip>
            )}
            {bucket.pt0_only && (
              <Tooltip title="A programtype=0 (Other/admin) row is audited to a different season than this bucket. Inert downstream — inspect if you like; not a structural problem.">
                <Chip size="small" color="default" variant="outlined" label="pt=0 check" />
              </Tooltip>
            )}
            {bucket.unaudited_count > 0 && (
              <Tooltip title={`${bucket.unaudited_count} assigned season(s) have no audited Tracking Season yet — audit them in the Audit view.`}>
                <Chip size="small" color="info" variant="outlined" label={`${bucket.unaudited_count} unaudited`} />
              </Tooltip>
            )}
          </Stack>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Stack spacing={0.5}>
            <TextField
              select
              size="small"
              value={editMapsTo ?? ""}
              onChange={(e) => onEditMapsToChange(e.target.value)}
              disabled={saving}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              label="Maps to"
            >
              <option value="">— (unmapped)</option>
              <option value="1">Spring</option>
              <option value="2">Summer</option>
              <option value="3">Fall</option>
              <option value="4">Winter</option>
              <option value="0">Other</option>
            </TextField>
            <Typography variant="caption" color="text.secondary">{inferred.label}</Typography>
            {mismatchWarning && (
              <Typography variant="caption" color="warning.main">{mismatchWarning}</Typography>
            )}
          </Stack>
        ) : (
          declaredMapsToLabel || <Typography variant="body2" color="warning.main">Needs mapping</Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2">{bucket.assigned_season_count}</Typography>
        {bucket.assigned_season_count > 0 && (
          <>
            {assignedDateRange && (
              <Typography variant="caption" color="text.secondary" display="block">
                {assignedDateRange}
              </Typography>
            )}
            {bucket.sample_season_names && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ fontStyle: "italic" }}
              >
                e.g. {bucket.sample_season_names}
              </Typography>
            )}
          </>
        )}
      </TableCell>
      <TableCell align="center">
        <Checkbox checked={!!bucket.is_active} disabled />
      </TableCell>
      <TableCell align="center">
        {isEditing ? (
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button size="small" variant="contained" onClick={onSaveClick} disabled={saveDisabled}>
              Save
            </Button>
            <Button size="small" variant="outlined" onClick={onCancelClick} disabled={saving}>
              Cancel
            </Button>
          </Stack>
        ) : (
          <>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button size="small" variant="outlined" onClick={onEditClick} disabled={isToggling || isDeleting}>
                Edit
              </Button>
              {isUnused ? (
                <Button size="small" variant="outlined" onClick={onDeleteClick} disabled={isDeleting}>
                  Delete
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onToggleActiveClick}
                  disabled={isToggling}
                >
                  {bucket.is_active ? "Deactivate" : "Reactivate"}
                </Button>
              )}
            </Stack>
            {actionError && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                {actionError}
              </Typography>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );
}
